import { useEffect, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { transformPrice, PriceRangeType } from './editBundle.vm.mapper';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BundleFormValues,
  formDefaultValues,
  bundlePriceFormSchema,
} from './priceFormSchemas';
import { useMigrateToBundleModel } from '../model/migrateToBundle.model';
import {
  getStepCta,
  mapInputToSetBundleBusinessModel,
} from './migrateToBundle.mapper';
import { ButtonVariant } from '@client/new/pages/workout/components/addFromLibrary/mappers';
import toast from 'react-hot-toast';
import { BundlesModel } from '../model/subscriptionBundles.model';
import { ButtonColor } from '@design-system/components/button/button.css';
import { trackEvent } from '@services/mixpanel';

type PayloadMap = {
  startMigration: null;
  setMigrationStep: MigrationModalSteps;
  migrate: null;
  closeMigration: null;
  cancelMigration: null;
  transformPrice: {
    value: string;
    type: PriceRangeType;
  };
  closeMigrationAlert: null;
  validatePrice: null;
};

export type MigrateToBundleAction = Actions<PayloadMap>;
export type MigrateToBundleDispatcher = FSpread<PayloadMap>;

export type MigrateToBundleVM = {
  migrateToBundle: {
    showMigrationAlert: boolean;
    onMigrationAlertClose: () => void;
    migrationModal: {
      isOpen: boolean;
      close: () => void;
      control: Control<BundleFormValues, any, BundleFormValues>;
      error?: string;
      transformPrice: (value: string, type: PriceRangeType) => void;
      step: MigrationModalSteps;
      stepCta: Array<{
        title: string;
        onClick: () => void;
        submitting?: boolean;
        variant?: ButtonVariant;
        className?: string;
        color?: ButtonColor;
      }>;
      freeTrial: {
        disabled: {
          monthly: boolean;
          annual: boolean;
        };
      };
    };
    startMigration: () => void;
  };
};

export type MigrationModalSteps = 1 | 2 | 3 | 4 | 5;
const migrationAlertKey = 'showMigrationAlert';

export const useMigrateToBundleVM = ({
  creatorId,
  model,
}: {
  creatorId: number;
  model: Async<BundlesModel>;
}): MigrateToBundleVM => {
  const [isSubmittingPrice, setSubmittingPrice] = useState(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [migrationStep, setMigrationStep] = useState<MigrationModalSteps>(1);
  const [showMigrationAlert, setShowMigrationAlert] = useState<boolean>(false);
  const { migrate } = useMigrateToBundleModel();
  const modelValue = model.type === 'fetched' ? model.value : null;
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    const migrationAlert = localStorage.getItem(migrationAlertKey);
    const havePlatformBundle = !!modelValue?.initialSubscriptionBundles?.some(
      (b) => b.isPlatformBundle,
    );
    if (migrationAlert && havePlatformBundle) {
      setShowMigrationAlert(true);
    }
  }, [modelValue]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BundleFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(bundlePriceFormSchema),
  });

  const annualPriceEnabled = watch('annualPriceEnabled');
  const monthlyPriceEnabled = watch('monthlyPriceEnabled');

  useEffect(() => {
    if (isFormInitialized) {
      trackEvent({
        type: 'Subscription price state toggled',
        payload: {
          type: annualPriceEnabled ? 'annual' : 'monthly',
          offerId: 0,
        },
      });
    }
  }, [annualPriceEnabled, monthlyPriceEnabled, isFormInitialized]);

  const onMigrate = handleSubmit(
    (values) => {
      const input = mapInputToSetBundleBusinessModel(values, creatorId);
      migrate(input)
        .then(() => {
          setShowMigrationAlert(true);
          localStorage.setItem(migrationAlertKey, 'true');
          modelValue?.refetch();
          modelValue?.refetchInfluencer();
          setModalOpen(false);
          setMigrationStep(1);
          if (input.annualPriceCents || input.monthlyPriceCents) {
            trackEvent({
              type: 'Subscription price saved',
              payload: {
                offerId: 0,
              },
            });
          }
        })
        .catch((e) => {
          toast.error(e.message);
        })
        .finally(() => {
          setSubmittingPrice(false);
        });
    },
    () => {
      setSubmittingPrice(false);
    },
  );

  const validatePriceStep = handleSubmit(() => {
    setMigrationStep(5);
  });

  const dispatch: MigrateToBundleDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as MigrateToBundleAction;
    switch (action.type) {
      case 'migrate':
        setSubmittingPrice(true);
        onMigrate();
        return null;
      case 'transformPrice':
        transformPrice(action.payload.value, action.payload.type, setValue);
        return null;
      case 'closeMigration':
      case 'cancelMigration':
        reset(formDefaultValues, {
          keepValues: false,
          keepDirty: false,
          keepDirtyValues: false,
        });
        setModalOpen(false);
        setIsFormInitialized(false);
        setMigrationStep(1);
        return null;
      case 'startMigration':
        trackEvent({
          type: 'Offer Exclusive Started',
          payload: {
            migrated: true,
          },
        });
        setIsFormInitialized(true);
        setModalOpen(true);
        return null;
      case 'setMigrationStep':
        setMigrationStep(action.payload);
        return null;
      case 'closeMigrationAlert':
        localStorage.removeItem(migrationAlertKey);
        setShowMigrationAlert(false);
        return null;
      case 'validatePrice':
        validatePriceStep();
        return null;
    }
  };

  return {
    migrateToBundle: {
      showMigrationAlert,
      startMigration: () => dispatch('startMigration'),
      onMigrationAlertClose: () => dispatch('closeMigrationAlert'),
      migrationModal: {
        isOpen: modalOpen,
        close: () => dispatch('cancelMigration'),
        control: control,
        error: errors.monthlyPriceEnabled?.message,
        transformPrice: (value: string, type: PriceRangeType) =>
          dispatch('transformPrice', { value, type }),
        step: migrationStep,
        stepCta: getStepCta({ migrationStep, dispatch, isSubmittingPrice }),
        freeTrial: {
          disabled: {
            monthly: !monthlyPriceEnabled,
            annual: !annualPriceEnabled,
          },
        },
      },
    },
  };
};
